import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { LanguageProvider } from './i18n/LanguageContext';
import { LocaleFromQuery } from './i18n/LocaleFromQuery';
import { SiteContentProvider } from './hooks/useSiteContent';
import { SiteLayout } from './components/SiteLayout';
import { HomePage } from './pages/HomePage';
import { ServicesPage } from './pages/ServicesPage';
import { AboutPage } from './pages/AboutPage';
import { PortFeaturesPage } from './pages/PortFeaturesPage';
import { SustainabilityPage } from './pages/SustainabilityPage';
import { NewsPage } from './pages/NewsPage';
import { NewsDetailPage } from './pages/NewsDetailPage';
import { CareersPage } from './pages/CareersPage';
import { ContactPage } from './pages/ContactPage';

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    // "/hizmetler#konteyner-operasyonlari" gibi linklerde hedef bolum icerik API'den
    // geldikten sonra olusur; kisa bir sure bekleyerek bolume kaydirilir.
    const id = decodeURIComponent(hash.slice(1));
    let attempts = 0;
    let found = false;
    let settleTicks = 0;
    let userScrolled = false;
    // Kullanici kendisi kaydirirsa duzeltme hemen biter
    const stopOnUserInput = () => {
      userScrolled = true;
    };
    const inputEvents = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;
    inputEvents.forEach((name) => window.addEventListener(name, stopOnUserInput, { passive: true }));
    const cleanup = () => {
      window.clearInterval(timer);
      inputEvents.forEach((name) => window.removeEventListener(name, stopOnUserInput));
    };
    // Hedef konum elle hesaplanip window.scrollTo ile gidilir: scrollIntoView bazi mobil
    // gorunumlerde (sayfa olcegi 1 degilken) pencereyi hic kaydirmiyordu. scroll-margin-top
    // (yapiskan baslik payi) hesaba katilir.
    const offsetOf = (target: HTMLElement) =>
      target.getBoundingClientRect().top - (parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0);
    const scrollToTarget = (target: HTMLElement, behavior: ScrollBehavior) =>
      window.scrollTo({ top: Math.max(0, window.scrollY + offsetOf(target)), behavior });
    const timer = window.setInterval(() => {
      const target = document.getElementById(id);
      attempts += 1;
      if (!found) {
        if (target) {
          found = true;
          scrollToTarget(target, 'smooth');
        } else if (attempts > 20) {
          cleanup();
        }
        return;
      }
      // Gorseller kaydirma sirasinda yuklenip bolumu asagi itebilir; yumusak kaydirma eski
      // hedefte kalir. ~2,5 sn boyunca bolum yerinden kaydiysa aninda yeniden hizalanir.
      settleTicks += 1;
      if (userScrolled || !target || settleTicks > 16) {
        cleanup();
        return;
      }
      if (settleTicks < 5) return; // yumusak kaydirmanin bitmesini bekle
      const offset = offsetOf(target);
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      if (Math.abs(offset) > 4 && !(offset > 0 && atBottom)) {
        scrollToTarget(target, 'auto');
      }
    }, 150);
    return cleanup;
  }, [pathname, hash]);
  return null;
}

export default function App() {
  return (
    <LanguageProvider>
      <SiteContentProvider>
        <LocaleFromQuery />
        <ScrollToTop />
        <SiteLayout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/hizmetler" element={<ServicesPage />} />
            <Route path="/hakkimizda" element={<AboutPage />} />
            <Route path="/liman-ozellikleri" element={<PortFeaturesPage />} />
            <Route path="/haberler" element={<NewsPage />} />
            <Route path="/haberler/:slug" element={<NewsDetailPage />} />
            <Route path="/surdurulebilirlik" element={<SustainabilityPage />} />
            <Route path="/kariyer" element={<CareersPage />} />
            <Route path="/iletisim" element={<ContactPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </SiteLayout>
      </SiteContentProvider>
    </LanguageProvider>
  );
}
